/**
 * LateExtrasPayload - the detail of a late_extras item (#2028): extras that
 * arrived after the invoice that would have carried them was sent, grouped by
 * child and invoice, with the two decisions. "Re-issue invoices" cancels each
 * affected invoice and creates a corrected draft that carries the extras;
 * "Skip" leaves them for the next invoice of each child.
 *
 * The generic action bar of the detail sheet is suppressed for this category:
 * both actions live here, next to the list they act on.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useCanOpen } from '@/hooks/useCanOpen';
import { useRegenerateLateExtras } from '@/hooks/useLateExtras';
import { formatMoneyCents } from '@/lib/money';
import { formatDate } from '@/utils/dateUtils';
import {
  dominantPeriod,
  formatPeriod,
  lateExtraKindLabel,
  lateExtrasSumText,
  parseLateExtrasPayload,
  type LateExtrasInvoiceGroup,
  type LateExtrasInvoiceResult,
  type LateExtrasItem,
} from '@/types/lateExtras';
import { TransitionDialog } from './TransitionDialog';

interface LateExtrasPayloadProps {
  payload: Record<string, unknown>;
  itemId?: string;
  currentState?: string;
}

/** The stable codes of a failed re-issue, as the late extras service writes them. */
const ERROR_MESSAGES: Record<string, MessageDescriptor> = {
  INVOICE_CORRECTION_VALIDATION_FAILED: msg({
    id: 'lateExtras.error.validation',
    message: 'The contract fails the billing checks for this month. Fix the contract, then try again.',
  }),
  INVOICE_CORRECTION_NO_BILLABLE: msg({
    id: 'lateExtras.error.noBillable',
    message: 'No contract version covers this month. Fix the contract dates, then try again.',
  }),
  INVOICE_PERIOD_ALREADY_LIVE: msg({
    id: 'lateExtras.error.periodLive',
    message: 'Another invoice for this month exists. Correct that invoice on the invoices page.',
  }),
  SUBSIDY_DECISION_PENDING: msg({
    id: 'lateExtras.error.subsidyPending',
    message: 'The subsidy decision is still pending for this contract. Enter it, then try again.',
  }),
  SUBSIDY_EXPIRED_HOLD: msg({
    id: 'lateExtras.error.subsidyExpired',
    message: 'The Beitragsfaktor or SBU confirmation has expired. Enter the renewal, then try again.',
  }),
  INVOICE_NOT_CORRECTABLE: msg({
    id: 'lateExtras.error.notCorrectable',
    message: 'This invoice can no longer be corrected (a credit note exists). Skip the item instead.',
  }),
  INVOICE_NOT_FOUND: msg({
    id: 'lateExtras.error.notFound',
    message: 'This invoice no longer exists. Try again: the list is read anew.',
  }),
};
const ERROR_FALLBACK = msg({
  id: 'lateExtras.error.generic',
  message: 'This invoice could not be re-issued. Try again.',
});

function errorText(i18n: { _: (d: MessageDescriptor) => string }, result: LateExtrasInvoiceResult): string {
  return i18n._(ERROR_MESSAGES[result.error_code ?? ''] ?? ERROR_FALLBACK);
}

function draftsLink(period: string): string {
  const [year, month] = period.split('-');
  const params = new URLSearchParams({ year, month: String(Number(month)), status: 'draft' });
  return `/billing/invoices?${params.toString()}`;
}

function invoiceLink(invoiceId: string): string {
  return `/billing/invoices?${new URLSearchParams({ invoice_id: invoiceId }).toString()}`;
}

function amountText(item: LateExtrasItem): string {
  return item.amount_rappen === null || item.amount_rappen === undefined ? '—' : formatMoneyCents(item.amount_rappen, null);
}

interface ChildGroup {
  childName: string;
  invoices: { group: LateExtrasInvoiceGroup; items: LateExtrasItem[] }[];
}

/** Groups the list the way the office reads it: child, then invoice, then date. */
function groupByChild(items: LateExtrasItem[], byInvoice: LateExtrasInvoiceGroup[]): ChildGroup[] {
  const groups = new Map(byInvoice.map((g) => [g.invoice_id, g]));
  const children = new Map<string, ChildGroup>();
  for (const item of items) {
    const key = item.child_id || item.child_name;
    let child = children.get(key);
    if (!child) {
      child = { childName: item.child_name, invoices: [] };
      children.set(key, child);
    }
    const invoiceId = item.would_have_been_on?.invoice_id ?? '';
    let entry = child.invoices.find((e) => e.group.invoice_id === invoiceId);
    if (!entry) {
      const group = groups.get(invoiceId) ?? {
        invoice_id: invoiceId,
        invoice_number: item.would_have_been_on?.invoice_number ?? '',
        period: item.would_have_been_on?.period ?? '',
        child_name: item.child_name,
        item_count: 0,
        sum_rappen: 0,
        has_unpriced: false,
        result: null,
      };
      entry = { group, items: [] };
      child.invoices.push(entry);
    }
    entry.items.push(item);
  }
  return [...children.values()];
}

export function LateExtrasPayload({ payload, itemId, currentState }: LateExtrasPayloadProps) {
  const { i18n } = useLingui();
  const canOpen = useCanOpen();
  const data = useMemo(() => parseLateExtrasPayload(payload), [payload]);
  const regenerate = useRegenerateLateExtras(itemId ?? '');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);

  const pending = currentState === 'pending';
  const childGroups = useMemo(() => groupByChild(data.items, data.by_invoice), [data]);
  const openInvoices = data.by_invoice.filter((g) => !g.result?.replacement_invoice_id);
  const failed = data.by_invoice.filter((g) => g.result?.error_code);
  const succeeded = data.by_invoice.filter((g) => g.result?.replacement_invoice_id);
  const draftPeriods = [...new Set(succeeded.map((g) => g.period))].sort();
  const canOpenInvoices = canOpen('/billing/invoices');
  const period = dominantPeriod(data.items);
  const where = period
    ? i18n._({ id: 'lateExtras.title.onePeriod', message: 'the {period} invoice', values: { period: formatPeriod(period, i18n.locale) } })
    : i18n._(msg({ id: 'lateExtras.title.severalInvoices', message: 'several invoices' }));
  const runningSince = data.regenerating?.at ? new Date(data.regenerating.at) : null;
  const running = !!runningSince && Date.now() - runningSince.getTime() < 10 * 60 * 1000;

  const onRegenerate = async () => {
    const res = await regenerate.mutateAsync();
    if (res.failed > 0) {
      toast.warning(
        i18n._({
          id: 'lateExtras.regenerate.partial',
          message: '{created, plural, one {# draft created} other {# drafts created}}, {failed, plural, one {# invoice failed} other {# invoices failed}}. The failed ones stay open.',
          values: { created: res.created, failed: res.failed },
        }),
      );
    } else if (res.created > 0) {
      toast.success(
        i18n._({
          id: 'lateExtras.regenerate.success',
          message: '{count, plural, one {# draft created} other {# drafts created}}. Finalise and send them via the «Month-end» bar.',
          values: { count: res.created },
        }),
      );
    } else {
      toast.info(i18n._(msg({ id: 'lateExtras.regenerate.nothing', message: 'Nothing to re-issue: the extras were invoiced meanwhile.' })));
    }
  };

  return (
    <div className="space-y-4" data-testid="late-extras-payload">
      <p className="text-sm font-medium" data-testid="late-extras-title">
        {pending
          ? i18n._({
              id: 'lateExtras.title',
              message: '{count, plural, one {# new extra that is not on {where}} other {# new extras that are not on {where}}}',
              values: { count: data.item_count, where },
            })
          : i18n._({
              id: 'lateExtras.title.closed',
              message: '{count, plural, one {# extra that was not on {where}} other {# extras that were not on {where}}}',
              values: { count: data.item_count, where },
            })}
      </p>

      {currentState === 'regenerated' && data.regenerate && (
        <Alert data-testid="late-extras-regenerated">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            {i18n._({
              id: 'lateExtras.state.regenerated',
              message: 'Re-issued on {date}: {count, plural, one {# draft} other {# drafts}}. Finalise and send them via the «Month-end» bar.',
              values: { date: formatDate(data.regenerate.at), count: data.regenerate.replacements },
            })}
          </AlertDescription>
        </Alert>
      )}
      {currentState === 'skipped' && (
        <Alert data-testid="late-extras-skipped">
          <AlertDescription>
            {data.resolved_by === 'system'
              ? i18n._(msg({ id: 'lateExtras.state.closedBySystem', message: 'Closed automatically: every extra was invoiced meanwhile.' }))
              : i18n._(msg({ id: 'lateExtras.state.skipped', message: 'Skipped: the extras are billed on the next invoice of each child.' }))}
          </AlertDescription>
        </Alert>
      )}
      {pending && running && (
        <Alert data-testid="late-extras-running">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <AlertDescription>
            {i18n._(msg({ id: 'lateExtras.state.running', message: 'The invoices are being re-issued. Reload in a moment.' }))}
          </AlertDescription>
        </Alert>
      )}
      {pending && failed.length > 0 && (
        <Alert variant="destructive" data-testid="late-extras-failed">
          <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          <AlertDescription>
            {i18n._({
              id: 'lateExtras.state.failed',
              message: '{count, plural, one {# invoice could not be re-issued} other {# invoices could not be re-issued}}. Fix the cause shown below, then try again: only the failed ones run again.',
              values: { count: failed.length },
            })}
          </AlertDescription>
        </Alert>
      )}

      {data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {i18n._(msg({ id: 'lateExtras.empty', message: 'No extras left on this item: they were invoiced meanwhile.' }))}
        </p>
      ) : (
        <div className="space-y-4">
          {childGroups.map((child) => (
            <section key={child.childName} className="space-y-2" aria-label={child.childName}>
              <h4 className="text-sm font-semibold">{child.childName}</h4>
              {child.invoices.map(({ group, items }) => (
                <div key={group.invoice_id} className="rounded-md border" data-testid={`late-extras-invoice-${group.invoice_id}`}>
                  <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2 text-xs">
                    <span className="font-medium">
                      {i18n._({
                        id: 'lateExtras.invoice.heading',
                        message: 'Invoice {number} · {period}',
                        values: { number: group.invoice_number, period: formatPeriod(group.period, i18n.locale) },
                      })}
                    </span>
                    {group.result?.replacement_invoice_id && (
                      <Badge variant="outline" className="gap-1" data-testid="late-extras-result-ok">
                        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                        {canOpenInvoices ? (
                          <Link to={invoiceLink(group.result.replacement_invoice_id)} className="underline underline-offset-2">
                            {i18n._({ id: 'lateExtras.result.draft', message: 'Draft {number}', values: { number: group.result.replacement_number ?? '' } })}
                          </Link>
                        ) : (
                          i18n._({ id: 'lateExtras.result.draft', message: 'Draft {number}', values: { number: group.result.replacement_number ?? '' } })
                        )}
                      </Badge>
                    )}
                    {group.result?.error_code && (
                      <Badge variant="destructive" data-testid="late-extras-result-error">
                        {i18n._(msg({ id: 'lateExtras.result.failed', message: 'Not re-issued' }))}
                      </Badge>
                    )}
                  </div>
                  {group.result?.error_code && (
                    <p className="px-3 pt-2 text-xs text-destructive" data-testid="late-extras-result-error-text">
                      {errorText(i18n, group.result)}
                    </p>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{i18n._(msg({ id: 'lateExtras.column.kind', message: 'Kind' }))}</TableHead>
                        <TableHead>{i18n._(msg({ id: 'lateExtras.column.date', message: 'Date' }))}</TableHead>
                        <TableHead>{i18n._(msg({ id: 'lateExtras.column.detail', message: 'Detail' }))}</TableHead>
                        <TableHead className="text-right">{i18n._(msg({ id: 'lateExtras.column.amount', message: 'Amount' }))}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item) => (
                        <TableRow key={`${item.kind}-${item.id}`}>
                          <TableCell>{lateExtraKindLabel(i18n, item.kind)}</TableCell>
                          <TableCell className="tabular-nums">{formatDate(item.service_date)}</TableCell>
                          <TableCell className="max-w-[16rem] truncate" title={item.label}>{item.label || '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{amountText(item)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </section>
          ))}
          <p className="flex justify-between text-sm font-medium" data-testid="late-extras-sum">
            <span>{i18n._(msg({ id: 'lateExtras.sum.label', message: 'Total' }))}</span>
            <span className="tabular-nums">{lateExtrasSumText(i18n, data.sum_rappen, data.has_unpriced)}</span>
          </p>
          {data.has_unpriced && (
            <p className="text-xs text-muted-foreground">
              {i18n._(msg({ id: 'lateExtras.sum.unpricedHint', message: '— marks an extra the invoice prices when it is created.' }))}
            </p>
          )}
        </div>
      )}

      {draftPeriods.length > 0 && canOpenInvoices && (
        <div className="flex flex-wrap gap-2" data-testid="late-extras-drafts">
          {draftPeriods.map((p) => (
            <Button key={p} asChild size="sm" variant="outline">
              <Link to={draftsLink(p)}>
                {draftPeriods.length === 1
                  ? i18n._(msg({ id: 'lateExtras.drafts.show', message: 'Show drafts' }))
                  : i18n._({ id: 'lateExtras.drafts.showPeriod', message: 'Show drafts {period}', values: { period: formatPeriod(p, i18n.locale) } })}
              </Link>
            </Button>
          ))}
        </div>
      )}

      {pending && itemId && (
        <div className="flex flex-wrap gap-2" data-testid="late-extras-actions">
          <Button onClick={() => setConfirmOpen(true)} disabled={running || openInvoices.length === 0}>
            <RefreshCw className="mr-1 h-4 w-4" aria-hidden="true" />
            {failed.length > 0
              ? i18n._(msg({ id: 'lateExtras.action.retry', message: 'Try again' }))
              : i18n._(msg({ id: 'lateExtras.action.regenerate', message: 'Re-issue invoices' }))}
          </Button>
          <Button variant="outline" onClick={() => setSkipOpen(true)} disabled={running}>
            {i18n._(msg({ id: 'lateExtras.action.skip', message: 'Skip' }))}
          </Button>
        </div>
      )}

      {itemId && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={i18n._(msg({ id: 'lateExtras.confirm.title', message: 'Re-issue these invoices?' }))}
          description={i18n._(
            msg({
              id: 'lateExtras.confirm.description',
              message: 'Each invoice is cancelled and re-issued as a draft with a new number that carries the extras. Payments already made stay with the family as credit. Then finalise and send the drafts via the «Month-end» bar.',
            }),
          )}
          confirmLabel={i18n._({
            id: 'lateExtras.confirm.action',
            message: '{count, plural, one {Re-issue # invoice} other {Re-issue # invoices}}',
            values: { count: openInvoices.length },
          })}
          onConfirm={onRegenerate}
          isPending={regenerate.isPending}
        >
          <ul className="space-y-1 text-sm" data-testid="late-extras-confirm-list">
            {openInvoices.map((g) => (
              <li key={g.invoice_id}>
                {i18n._({
                  id: 'lateExtras.confirm.invoice',
                  message: '{number} · {child} · {period}',
                  values: { number: g.invoice_number, child: g.child_name, period: formatPeriod(g.period, i18n.locale) },
                })}
              </li>
            ))}
          </ul>
        </ConfirmDialog>
      )}
      {itemId && (
        <TransitionDialog
          open={skipOpen}
          onOpenChange={setSkipOpen}
          itemId={itemId}
          action="skip"
          currentState={currentState ?? 'pending'}
          title={i18n._(msg({ id: 'lateExtras.skip.title', message: 'Skip these extras?' }))}
          summary={i18n._(
            msg({
              id: 'lateExtras.skip.summary',
              message: 'The sent invoices stay as they are. The extras are billed on the next invoice of each child.',
            }),
          )}
          confirmLabel={i18n._(msg({ id: 'lateExtras.action.skip', message: 'Skip' }))}
        />
      )}
    </div>
  );
}
